import React, { useState } from 'react';
import {
  Tooltip,
  IconButton,
  useMediaQuery,
  useTheme,
  ClickAwayListener,
  Box,
  Typography,
  Link,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useNavigate } from 'react-router-dom';

interface HelpTooltipProps {
  title: string;
  iconSize?: number;
  /** Deep link to help article - format: "categorySlug/articleSlug" (e.g., "store-hours/special-hours") */
  learnMoreLink?: string;
  /** Custom label for learn more link */
  learnMoreLabel?: string;
}

/**
 * Responsive help tooltip component.
 * - Mobile: Tap icon to show, tap elsewhere to dismiss
 * - Desktop: Hover to show
 * - Supports "Learn more" links to Help Center articles
 */
export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  title,
  iconSize = 18,
  learnMoreLink,
  learnMoreLabel = 'Learn more',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const handleTooltipToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleLearnMoreClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    // Navigate to help center with article context
    // Store the target article in sessionStorage for the HelpCenter to pick up
    if (learnMoreLink) {
      sessionStorage.setItem('helpCenterTarget', learnMoreLink);
      navigate('/help');
    }
  };

  // Render tooltip content with optional "Learn more" link
  const tooltipContent = learnMoreLink ? (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Link
        component="button"
        variant="body2"
        onClick={handleLearnMoreClick}
        sx={{
          color: 'primary.light',
          textDecoration: 'underline',
          cursor: 'pointer',
          fontSize: '0.8rem',
          '&:hover': {
            color: 'primary.main',
          },
        }}
      >
        {learnMoreLabel} →
      </Link>
    </Box>
  ) : (
    title
  );

  // Mobile: Click-controlled tooltip
  if (isMobile) {
    return (
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <Tooltip
          title={tooltipContent}
          open={open}
          onClose={handleTooltipClose}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '0.875rem',
                maxWidth: 280,
                p: 1.5,
              },
            },
          }}
        >
          <IconButton
            onClick={handleTooltipToggle}
            size="small"
            sx={{
              p: 0.5,
              ml: 0.5,
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
            aria-label="Help"
          >
            <HelpOutlineIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
      </ClickAwayListener>
    );
  }

  // Desktop: Hover-controlled tooltip (but click if has learn more link)
  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        title={tooltipContent}
        open={open}
        onOpen={learnMoreLink ? undefined : handleTooltipOpen}
        onClose={handleTooltipClose}
        disableFocusListener={!!learnMoreLink}
        disableHoverListener={!!learnMoreLink}
        disableTouchListener={!!learnMoreLink}
        arrow
        placement="top"
        slotProps={{
          tooltip: {
            sx: {
              fontSize: '0.875rem',
              maxWidth: 280,
              p: 1.5,
            },
          },
        }}
      >
        <IconButton
          onClick={learnMoreLink ? handleTooltipToggle : undefined}
          onMouseEnter={learnMoreLink ? undefined : handleTooltipOpen}
          onMouseLeave={learnMoreLink ? undefined : handleTooltipClose}
          size="small"
          sx={{
            p: 0.5,
            ml: 0.5,
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
          }}
          aria-label="Help"
        >
          <HelpOutlineIcon sx={{ fontSize: iconSize }} />
        </IconButton>
      </Tooltip>
    </ClickAwayListener>
  );
};

export default HelpTooltip;
